/**
 * Programma built-in — contenuto di catalogo, NON dati utente (come i compendi: modello-dati
 * §2.2 nota "compendium.item non è un evento"). La sorgente è il programma REALE del
 * proprietario: V-Taper V3.2.2, `Ipertrofia/10-programma/current.md` + schede
 * `Ipertrofia/20-mesociclo-corrente/day-{A,B,C}.md` (fixture di produzione, regola 8).
 *
 * `exercise_id` = slug stabile del catalogo (eccezione D14: "i compendi si agganciano tra
 * installazioni"). I template come eventi (`template.created`) arrivano con M4 (builder D10):
 * qui il contenuto serve a M1 (template di oggi, pre-fill, export leggibile).
 *
 * Prescrizioni: D16 (fisso = `min=max` sullo stesso campo). Recupero: dal programma reale
 * (Rec. della scheda; i range 60-75s si prendono al valore alto = il recupero che serve).
 */
import { t } from "../i18n/it";

export interface EsercizioTemplate {
  exercise_id: string; // slug stabile catalogo (D14)
  nome: string;
  sets: number;
  reps_min: number;
  reps_max: number;
  rir_min: number;
  rir_max: number;
  rest_sec: number;
  cue: string;
  // contratto §1.5: `barbell` | `bodyweight` | `bodyweight_added` (default "barbell" = carico
  // esterno — anche cavi/macchine). Solo gli esercizi a corpo-peso lo dichiarano: è lo stesso
  // meccanismo dei default del contratto (§1.6 `mode`). L'utente lo cambia con 1 tap in sessione.
  load_type?: "barbell" | "bodyweight" | "bodyweight_added";
}

/** Tipo di carico effettivo dell'esercizio (default: barbell, v. commento al campo). */
export function caricoDiDefault(e: EsercizioTemplate): "barbell" | "bodyweight" | "bodyweight_added" {
  return e.load_type ?? "barbell";
}

/** Prescrizione compatta per la riga di Oggi: `4 × 6-8 · RIR 1-2 · 2 min` (D16: fisso = min=max). */
export function prescrizione(e: EsercizioTemplate): string {
  const reps = e.reps_min === e.reps_max ? String(e.reps_min) : `${e.reps_min}-${e.reps_max}`;
  const rir = e.rir_min === e.rir_max ? String(e.rir_min) : `${e.rir_min}-${e.rir_max}`;
  const min = e.rest_sec % 60 === 0 ? `${e.rest_sec / 60} ${t("unitMin")}` : `${e.rest_sec}${t("unitSec")}`;
  return `${e.sets} × ${reps} · ${t("rir")} ${rir} · ${min}`;
}

export interface GiornoTemplate {
  day_label: string; // §1.4 session.created: "Day A"
  titolo: string; // es. "V-Taper Priority"
  weekday: number; // 1 = lunedì … 7 = domenica (ISO 8601)
  esercizi: EsercizioTemplate[];
}

export const NOME_PROGRAMMA = "V-Taper V3";

export const GIORNI: GiornoTemplate[] = [
  {
    day_label: "Day A",
    titolo: "V-Taper Priority",
    weekday: 1,
    esercizi: [
      {
        exercise_id: "ex_incline_db_press",
        nome: "Panca inclinata con manubri (20-30°)",
        sets: 4,
        reps_min: 6,
        reps_max: 8,
        rir_min: 1,
        rir_max: 2,
        rest_sec: 120,
        cue: "Pausa 1s in basso in stretch, no rimbalzo. Manubri > bilanciere.",
      },
      {
        exercise_id: "ex_pull_up_prona",
        nome: "Trazioni presa prona larga",
        sets: 4,
        reps_min: 6,
        reps_max: 10,
        rir_min: 1,
        rir_max: 2,
        rest_sec: 120,
        cue: "Allungamento completo in basso, no kipping. Straps se il grip cede prima del dorsale.",
        load_type: "bodyweight_added",
      },
      {
        exercise_id: "ex_cable_lateral_raise",
        nome: "Alzate laterali ai cavi",
        sets: 4,
        reps_min: 12,
        reps_max: 20,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 60,
        cue: "Cavo dietro la schiena, conduci col gomito (mano passiva), stop a 90°. Polso neutro.",
      },
      {
        exercise_id: "ex_chest_supported_row",
        nome: "Rematore al petto (chest-supported)",
        sets: 3,
        reps_min: 8,
        reps_max: 10,
        rir_min: 1,
        rir_max: 1,
        rest_sec: 90,
        cue: "Tira ai fianchi (basso-sterno), gomiti indietro, scapole insieme. Straps.",
      },
      {
        exercise_id: "ex_seated_leg_curl",
        nome: "Leg curl seduto",
        sets: 3,
        reps_min: 10,
        reps_max: 12,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 75,
        cue: "Eccentrica controllata 2-3s, allungamento pieno.",
      },
      {
        exercise_id: "ex_cable_crunch",
        nome: "Crunch ai cavi",
        sets: 3,
        reps_min: 10,
        reps_max: 15,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 60,
        cue: "Flessione spinale (curva la colonna), non flessione d'anca. Dolore lombare = fermati.",
      },
    ],
  },
  {
    day_label: "Day B",
    titolo: "Compound Lower + Tirata Orizzontale",
    weekday: 3,
    esercizi: [
      {
        exercise_id: "ex_power_squat",
        nome: "Power squat / hack squat / pendulum squat",
        sets: 4,
        reps_min: 6,
        reps_max: 10,
        rir_min: 1,
        rir_max: 2,
        rest_sec: 120,
        cue: "Piedi larghezza spalle, discesa controllata, tallone giù. Stop prima del butt wink.",
      },
      {
        exercise_id: "ex_rdl",
        nome: "Stacco rumeno (RDL)",
        sets: 3,
        reps_min: 6,
        reps_max: 8,
        rir_min: 2,
        rir_max: 2,
        rest_sec: 120,
        cue: "Carico onesto, schiena neutra in ogni rep. 2 RIR pieni. Mai cercare il PR.",
      },
      {
        exercise_id: "ex_lat_pulldown_neutral",
        nome: "Lat machine presa larga neutra",
        sets: 3,
        reps_min: 10,
        reps_max: 12,
        rir_min: 1,
        rir_max: 1,
        rest_sec: 90,
        cue: "Allungamento pieno in alto (lockout vero). Tira al petto alto, gomiti verso il fianco.",
      },
      {
        exercise_id: "ex_incline_machine_chest_press",
        nome: "Chest press inclinato alla macchina",
        sets: 3,
        reps_min: 8,
        reps_max: 10,
        rir_min: 1,
        rir_max: 1,
        rest_sec: 90,
        cue: "Mantenimento petto alto, pausa breve in stretch.",
      },
      {
        exercise_id: "ex_face_pull",
        nome: "Face pull (corda, gomiti alti)",
        sets: 3,
        reps_min: 15,
        reps_max: 20,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 75,
        cue: "Corda all'altezza del viso, gomiti alti. A fine corsa scapole indietro E in basso.",
      },
      {
        exercise_id: "ex_cable_lateral_raise",
        nome: "Alzate laterali ai cavi (3ª dose settimanale)",
        sets: 2,
        reps_min: 15,
        reps_max: 20,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 60,
        cue: "Stesso carico di Day A/C (stesso RIR target). Stesse cue del Day A.",
      },
      {
        exercise_id: "ex_calf_raise_standing",
        nome: "Calf in piedi",
        sets: 4,
        reps_min: 10,
        reps_max: 12,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 75,
        cue: "Pausa 1-2s in massimo allungamento (bottom).",
        load_type: "bodyweight",
      },
    ],
  },
  {
    day_label: "Day C",
    titolo: "Spinta Macchina + Posteriore",
    weekday: 5,
    esercizi: [
      {
        exercise_id: "ex_machine_chest_press",
        nome: "Chest press convergente alla macchina",
        sets: 3,
        reps_min: 8,
        reps_max: 12,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 90,
        cue: "Squeeze a fine corsa, tensione continua. Macchina = stabile.",
      },
      {
        exercise_id: "ex_seal_row",
        nome: "Seal row / rematore al petto",
        sets: 3,
        reps_min: 10,
        reps_max: 12,
        rir_min: 1,
        rir_max: 1,
        rest_sec: 90,
        cue: "Tira al basso-sterno, pausa 1s in contrazione, scapole insieme. Focus spessore.",
      },
      {
        exercise_id: "ex_lat_pulldown_stretch",
        nome: "Lat machine presa larga (stretch bias)",
        sets: 3,
        reps_min: 10,
        reps_max: 12,
        rir_min: 1,
        rir_max: 1,
        rest_sec: 90,
        cue: "Allungamento pieno in alto (braccia distese). Gomiti verso il fianco, no slancio col busto.",
      },
      {
        exercise_id: "ex_bulgarian_split_squat",
        nome: "Bulgarian split squat",
        sets: 3,
        reps_min: 8,
        reps_max: 10,
        rir_min: 1,
        rir_max: 2,
        rest_sec: 120,
        cue: "Busto leggermente avanti = più gluteo/femorale. Piede posteriore stabile.",
      },
      {
        exercise_id: "ex_seated_leg_curl",
        nome: "Leg curl seduto (pump)",
        sets: 3,
        reps_min: 10,
        reps_max: 15,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 75,
        cue: "Eccentrica controllata. Dose pump: reps più alte del Day A.",
      },
      {
        exercise_id: "ex_cable_lateral_raise",
        nome: "Alzate laterali ai cavi (variante)",
        sets: 3,
        reps_min: 15,
        reps_max: 20,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 60,
        cue: "Variante diversa dal Day A (es. lean-away unilaterale). Cavo dietro, conduci col gomito.",
      },
      {
        exercise_id: "ex_bayesian_curl",
        nome: "Bayesian curl",
        sets: 3,
        reps_min: 10,
        reps_max: 12,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 75,
        cue: "Gomito dietro al corpo = bicipite allungato. Cavo basso, gomito fisso.",
      },
      {
        exercise_id: "ex_overhead_cable_extension",
        nome: "French press alla corda (overhead)",
        sets: 3,
        reps_min: 10,
        reps_max: 12,
        rir_min: 0,
        rir_max: 1,
        rest_sec: 75,
        cue: "Mani sopra la testa, gomiti in alto e fissi. Capo lungo del tricipite in allungamento.",
      },
    ],
  },
];

/**
 * Slot del giorno ISO (`weekday`: 1=lunedì … 7=domenica). `null` = giorno senza slot
 * (il programma reale è Lun-Mer-Ven). Mai sorprese: il chiamante dichiara lo stato.
 */
export function slotDelGiorno(weekday: number): GiornoTemplate | null {
  return GIORNI.find((g) => g.weekday === weekday) ?? null;
}

/**
 * Template della sessione: lo slot della data (`YYYY-MM-DD`), altrimenti lo slot seguente
 * in settimana (deterministico: il prossimo slot dopo il weekday della data). Serve a Oggi
 * nei giorni di riposo — la UI etichetta la differenza ("prossimo slot").
 */
export function giornoDellaData(date: string): { giorno: GiornoTemplate; oggi: boolean } | null {
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  const weekday = parsed.getUTCDay() === 0 ? 7 : parsed.getUTCDay(); // 1..7 (ISO)
  const oggi = slotDelGiorno(weekday);
  if (oggi) return { giorno: oggi, oggi: true };
  for (let delta = 1; delta <= 7; delta += 1) {
    const prossimo = slotDelGiorno(((weekday - 1 + delta) % 7) + 1);
    if (prossimo) return { giorno: prossimo, oggi: false };
  }
  return null; // impossibile: GIORNI non è vuoto
}

/** Nome leggibile dell'esercizio dal catalogo; `null` = slug sconosciuto (fallback: l'ID). */
export function nomeEsercizio(exercise_id: string): string | null {
  for (const g of GIORNI) {
    for (const e of g.esercizi) {
      if (e.exercise_id === exercise_id) return e.nome;
    }
  }
  return null;
}

/**
 * Nome dell'esercizio CONTESTUALIZZATO al giorno: gli esercizi condivisi tra giorni hanno
 * nomi-variante (es. "Alzate laterali ai cavi (3ª dose settimanale)") e il primo match globale
 * rendeva quei nomi irraggiungibili nell'export MD. Priorità: nome del giorno della sessione,
 * poi il primo match del catalogo, poi `null` (fallback: lo slug).
 */
export function nomeEsercizioPerGiorno(giorno: GiornoTemplate | null, exercise_id: string): string | null {
  const inGiorno = giorno === null ? null : esercizioDelGiorno(giorno, exercise_id);
  return inGiorno !== null ? inGiorno.nome : nomeEsercizio(exercise_id);
}

/** Prescrizione dell'esercizio in un giorno di template (per pre-fill e riga Log). */
export function esercizioDelGiorno(giorno: GiornoTemplate, exercise_id: string): EsercizioTemplate | null {
  return giorno.esercizi.find((e) => e.exercise_id === exercise_id) ?? null;
}
